import useUpdateEffect from '@pretto/bricks/core/utility/useUpdateEffect'

import {
  Illustration,
  InputComponent,
} from '@pretto/website/src/templates/blocs/components/simulatorRow/SimulatorRow.types'
import { formatCloudFunctionRatesToAdvancedSimuRates } from '@pretto/website/src/templates/simulateurAvance/lib/formatCloudFunctionRatesToAdvancedSimuRates'
import { useTracking } from '@pretto/website/src/utilities'

import { useEffect, useMemo, useRef, useState } from 'react'

import * as INPUTS from '../config/inputs'
import * as calc from '../lib/calc'
import { getDefaultIncome, getMatch } from '../lib/helpers'

import { useRadio } from './useRadio'
import { useSlider } from './useSlider'

export const useCapacity = ({ trackButtonClick }, marketRatesProfileLatest) => {
  const trackAction = useTracking()
  const miniSimulator = formatCloudFunctionRatesToAdvancedSimuRates(marketRatesProfileLatest)
  const [rate, setRate] = useState(0)
  const [payment, setPayment] = useState(0)
  const [interests, setInterests] = useState(0)
  const [loanAmount, setLoanAmount] = useState(0)

  const handleClickResultsButton = () => {
    trackButtonClick('results')
  }

  const {
    value: numberMortgagors,
    props: numberMortgagorsProps,
    inputType: numberMortgagorsInputType,
  } = useRadio('numberMortgagors', { value: '1' })

  const {
    value: duration,
    debouncedValue: debouncedDuration,
    onChangeInput: onChangeDuration,
    props: durationProps,
    inputType: durationInputType,
  } = useSlider('duration')

  const defaultIncome = getDefaultIncome()
  const {
    value: income,
    debouncedValue: debouncedIncome,
    props: incomeProps,
    inputType: incomeInputType,
  } = useSlider('income', { value: defaultIncome })

  const lastChangedInputTypes = {
    duration: durationInputType,
    income: incomeInputType,
    numberMortgagors: numberMortgagorsInputType,
  }

  const totalAmount = calc.capacityTotalAmount({ duration, income })

  const previousData = useRef({ duration, income, numberMortgagors })

  const match = () => getMatch({ duration, income, miniSimulator, numberMortgagors })

  useEffect(() => {
    const { rate } = match()
    const payment = calc.capacityPayment({ income })
    const loanAmount = calc.capacityLoanAmount({ duration, payment, rate })
    const interests = totalAmount - loanAmount

    setLoanAmount(loanAmount)
    setRate(rate)
    setPayment(payment)
    setInterests(interests)
  }, [])

  useUpdateEffect(() => {
    const { max, min } = INPUTS.duration
    if (debouncedDuration < min || !debouncedDuration) {
      onChangeDuration(min)
      return
    }
    if (debouncedDuration > max) {
      onChangeDuration(max)
    }
  }, [debouncedDuration])

  useUpdateEffect(() => {
    const changes = { duration, income, numberMortgagors }
    const valueType = Object.entries(changes).reduce((previous, [key, value]) => {
      if (value !== previousData.current[key]) {
        return key
      }

      return previous
    }, false)

    previousData.current = changes

    if (valueType) {
      const inputType = lastChangedInputTypes[valueType]

      trackAction(`Calculator ${inputType} ${valueType} changed`, {
        calculatorVersion: 'v2',
        [valueType]: parseInt(changes[valueType]),
      })
    }

    if (duration > INPUTS.duration.max || duration < INPUTS.duration.min || income < 0) {
      return
    }

    const { rate } = match(income)
    const payment = calc.capacityPayment({ income })
    const loanAmount = calc.capacityLoanAmount({ duration, payment, rate })
    const interests = totalAmount - loanAmount

    setLoanAmount(loanAmount)
    setRate(rate)
    setPayment(payment)
    setInterests(interests)
  }, [debouncedDuration, debouncedIncome, numberMortgagors])

  const inputBlocks = [
    {
      illustration: parseInt(numberMortgagors) === 1 ? Illustration.SINGLE : Illustration.COUPLE,
      inputs: [
        {
          props: numberMortgagorsProps,
          type: InputComponent.RADIO,
        },
        {
          props: incomeProps,
          type: InputComponent.SLIDER,
        },
        {
          props: durationProps,
          type: InputComponent.SLIDER,
        },
      ],
      title: 'Votre projet',
    },
  ]

  const errorFlag = useMemo(() => (parseInt(income) <= 0 ? 'noIncome' : ''), [debouncedIncome])

  const resultProps = useMemo(
    () => ({
      capacity: loanAmount,
      creditCost: interests,
      duration,
      onClickButton: handleClickResultsButton,
      payment,
      rate,
    }),
    [interests, debouncedDuration, rate, payment, loanAmount]
  )
  return { errorFlag, graphProps: null, inputBlocks, isGraphActive: false, resultProps }
}
